import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { ApiService, AoiBounds, MaritimeRequestResponse, VesselSearchCandidate } from 'src/app/core/services/api.service';
import { Package } from 'src/app/core/models/package';

export type MaritimeRequestDialogData =
  | { kind: 'dark_ship'; aoi: AoiBounds }
  | { kind: 'product_request'; products: Package[]; query?: string; vesselContext?: Partial<VesselSearchCandidate> };

@Component({
  selector: 'app-maritime-request-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule],
  template: `
    <div class="request-dialog">
      <p class="request-dialog__kicker">TEVUNA REQUEST</p>
      <h2 mat-dialog-title>{{ data.kind === 'dark_ship' ? 'Dark Ship intelligence request' : 'Request a Tevuna Intelligence Product' }}</h2>
      <div mat-dialog-content>
        <ng-container *ngIf="data.kind === 'dark_ship'; else productForm">
          <p>Selecting this area creates a manual request. It does not spend credits or start a report pipeline.</p>
          <dl class="aoi-values"><div><dt>North</dt><dd>{{ data.aoi.north | number:'1.4-4' }}</dd></div><div><dt>South</dt><dd>{{ data.aoi.south | number:'1.4-4' }}</dd></div><div><dt>West</dt><dd>{{ data.aoi.west | number:'1.4-4' }}</dd></div><div><dt>East</dt><dd>{{ data.aoi.east | number:'1.4-4' }}</dd></div></dl>
        </ng-container>
        <ng-template #productForm>
          <p>Tell us which intelligence product you need for the vessel returned by your search.</p>
          <label>Product<select [(ngModel)]="productCode"><option value="" disabled>Select a product</option><option *ngFor="let product of productOptions" [value]="product.code">{{ product.name }}</option></select></label>
          <label>7-digit IMO (optional)<input [(ngModel)]="imo" inputmode="numeric" maxlength="7" placeholder="e.g. 9320843" /></label>
          <p class="request-dialog__validation" *ngIf="imo && !isValidImo">Enter a valid 7-digit IMO.</p>
        </ng-template>
        <p class="request-dialog__error" *ngIf="errorMessage" role="alert">{{ errorMessage }}</p>
        <p class="request-dialog__success" *ngIf="successMessage" role="status">{{ successMessage }}</p>
      </div>
      <div mat-dialog-actions align="end">
        <button mat-button type="button" (click)="close()">Cancel</button>
        <button mat-flat-button color="primary" type="button" [disabled]="loading || !canSubmit" (click)="submit()">{{ loading ? 'Sending…' : (data.kind === 'dark_ship' ? 'Send Dark Ship request' : 'Send request') }}</button>
      </div>
    </div>
  `,
  styles: [`
    :host { display:block; color:#d8f4ff; } .request-dialog { min-width: min(560px, 82vw); background:#031d2a; padding:8px; }
    h2 { color:#f5fbff; } p { color:#a8c6d2; } label { display:grid; gap:6px; margin:16px 0; color:#d8f4ff; font-weight:600; }
    input, select { background:#062b3d; border:1px solid #1a799d; border-radius:8px; color:#fff; padding:11px; font:inherit; }
    .aoi-values { display:grid; grid-template-columns:repeat(2,1fr); gap:10px; } .aoi-values div { background:#062b3d; border-radius:8px; padding:12px; }
    dt { color:#82b4c6; font-size:.75rem; text-transform:uppercase; } dd { margin:4px 0 0; font-weight:700; }
    .request-dialog__error, .request-dialog__validation { color:#ffb0bd; } .request-dialog__success { color:#53e6c1; }
  `]
})
export class MaritimeRequestDialogComponent {
  productCode = '';
  imo = '';
  loading = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MaritimeRequestDialogData,
    private dialogRef: MatDialogRef<MaritimeRequestDialogComponent>,
    private api: ApiService
  ) {
    if (data.kind === 'product_request') {
      this.productCode = data.products[0]?.code ?? '';
      this.imo = (data.query ?? '').replace(/^IMO\s*/i, '').replace(/\D/g, '').slice(0, 7);
    }
  }

  get isValidImo(): boolean { return /^\d{7}$/.test(this.imo); }
  get productOptions(): Package[] { return this.data.kind === 'product_request' ? this.data.products : []; }
  get canSubmit(): boolean { return this.data.kind === 'dark_ship' || (!!this.productCode && (!this.imo || this.isValidImo)); }

  submit(): void {
    if (!this.canSubmit || this.loading) return;
    const payload = this.data.kind === 'dark_ship'
      ? { kind: 'dark_ship' as const, aoi: this.data.aoi }
      : { kind: 'product_request' as const, productCode: this.productCode, imo: this.imo || null, vesselContext: this.data.vesselContext };
    this.loading = true;
    this.errorMessage = '';
    this.api.submitMaritimeRequest(payload).subscribe({
      next: (response: MaritimeRequestResponse) => {
        this.loading = false;
        this.successMessage = `Request accepted (${response.requestId}).`;
        this.dialogRef.close(response);
      },
      error: error => {
        this.loading = false;
        this.errorMessage = error?.status === 503 ? 'The request service is temporarily unavailable. Try again.' :
          error?.status === 400 ? 'The request details are invalid.' : 'Unable to send the request. Try again.';
      }
    });
  }

  close(): void { this.dialogRef.close(); }
}
